import * as d3 from 'd3';
import { PrimaryColor, BorderColor2, SidelyBlack } from '../../../styles/global/css/Utils';
import { createLegend, createTooltip, displayDateFormat, formatValue, getGroupColor, GroupedValueNameData, hideTooltip, positionTooltip, roundDigitsAfterDecimal, setupTooltipHiding, shouldShowLegend, trimLabel } from '../cross_platform/dashboard_script';

interface ElementWithCleanup extends Element {
	_cleanupTooltip?: () => void;
}

const BAR_RADIUS = 6;
const LABEL_HEIGHT = 16;
const MIN_BAND_WIDTH = 64;
const LABEL_CHAR_WIDTH = 6;

export function createGroupedBarSvg({
	chartData,
	granularity,
	width,
	height,
	marginTop,
	marginBottom,
	marginLeft,
	marginRight,
	opts,
	tooltipId
}: {
	chartData: GroupedValueNameData[],
	granularity?: 'day' | 'week' | 'month' | 'year',
	width: number,
	height: number,
	marginTop: number,
	marginBottom: number,
	marginLeft: number,
	marginRight: number,
	opts?: {
		locale?: string,
		orientation?: 'vertical' | 'horizontal';
		sort?: 'asc' | 'desc' | 'none',
		number_extension?: string | number,
		digits_after_decimal?: number,
		legend?: boolean,
		legendItemSpacing?: number,
		max_legend_items?: number,
		legend_orientation?: 'horizontal' | 'vertical',
		stacked?: boolean,
		trim_x_labels?: number,
		trim_y_labels?: number,
		x_axis_legend?: string,
		y_axis_legend?: string,
		x_axis_boundaries?: { min?: number, max?: number },
		y_axis_boundaries?: { min?: number, max?: number }
	},
	tooltipId?: string
}): SVGSVGElement | null {
	// parse date strings like "2025-12-01" and create a displayName using granularity
	const parseDate = d3.utcParse('%Y-%m-%d');
	const gran = granularity ?? 'month';
	const locale = opts?.locale ?? 'fr-FR';

	// enrich data with parsed date and computed displayName (fall back to original name)
	const enriched = chartData.map((d: GroupedValueNameData) => {
		const date = parseDate(String(d.name));
		const displayName = (date instanceof Date && !isNaN(+date)) ? displayDateFormat(date, gran, locale) : String(d.name);
		return { ...d, date, displayName } as GroupedValueNameData & { date?: Date; displayName: string };
	});

	// preserve original ordering or allow sort if provided (applies to names grouping).
	const sortOrder = opts?.sort ?? 'none';
	const uniqueNames = Array.from(new Set(enriched.map(d => d.displayName)));
	const hasValidDates = enriched.some(d => d.date instanceof Date && !isNaN(+d.date));

	const namesOrder = sortOrder === 'none' || hasValidDates
		? uniqueNames
		: uniqueNames.sort((a, b) => {
			// compute aggregate per displayName for sorting
			const sumFor = (name: string) => d3.sum(enriched.filter(d => d.displayName === name), d => d.value);
			return sortOrder === 'asc' ? sumFor(a) - sumFor(b) : sumFor(b) - sumFor(a);
		});

	// Both x and color encode the group class.
	const groupsArr = Array.from(new Set(enriched.map(d => d.group)));

	// Value labels are drawn for stacked totals and single-group charts; make sure each band can fit the widest one.
	const showsValueLabels = Boolean(opts?.stacked) || groupsArr.length === 1;
	const labelValues = opts?.stacked
		? namesOrder.map(name => d3.sum(enriched.filter(d => d.displayName === name), d => d.value))
		: enriched.map(d => d.value);
	const widestValueLabel = showsValueLabels
		? Math.max(0, ...labelValues.map(v => formatValue(roundDigitsAfterDecimal(v, opts?.digits_after_decimal), opts).length)) * LABEL_CHAR_WIDTH + 8
		: 0;
	const minBandWidth = Math.max(MIN_BAND_WIDTH, widestValueLabel);
	const drawWidth = Math.max(width, marginLeft + marginRight + namesOrder.length * minBandWidth);
	const fx = d3.scaleBand()
		.domain(namesOrder)
		.rangeRound([marginLeft, drawWidth - marginRight])
		.paddingInner(0.3);

	// Compute total per group for legend sorting
	const sums = groupsArr.map(group => ({
		key: group,
		total: d3.sum(enriched.filter(d => d.group === group), d => d.value)
	}));
	const sortedKeys = sums.sort((a, b) => b.total - a.total).map(s => s.key);

	// Prepare data for stacking vs grouping
	let stackedData: d3.Series<{ [key: string]: number }, string>[] | null = null;
	let stackTotals: number[] = [];
	let maxYValue = 0;

	if (opts?.stacked) {
		// Create matrix for stacking: rows = displayNames, columns = groups
		const matrix = namesOrder.map(name => {
			const row: { [key: string]: number } = {};
			row['name'] = 0; // This is just a placeholder, won't be used in stacking
			groupsArr.forEach(group => {
				const found = enriched.find(d => d.displayName === name && d.group === group);
				row[group] = found ? found.value : 0;
			});
			return row;
		});

		// Create stack generator
		const stack = d3.stack<{ [key: string]: number }>()
			.keys(sortedKeys)
			.value((d, key) => d[key] || 0);

		stackedData = stack(matrix);
		maxYValue = d3.max(stackedData, series => d3.max(series, d => d[1])) ?? 0;
		stackTotals = namesOrder.map((_, i) => d3.max(stackedData!, series => series[i]?.[1] ?? 0) ?? 0);
	} else {
		maxYValue = d3.max(enriched, d => d.value) ?? 0;
	}

	const x = d3.scaleBand()
		.domain(groupsArr)
		.rangeRound([0, fx.bandwidth()])
		.padding(0.15);

	// Add legend if requested and calculate adjusted margin
	const showLegend = shouldShowLegend(opts, sortedKeys);
	const chartMarginTop = marginTop + (showLegend ? 15 : 0);

	// Y encodes the height of the bar.
	const yMin = opts?.y_axis_boundaries?.min !== undefined ? opts.y_axis_boundaries.min : 0;
	const yMax = opts?.y_axis_boundaries?.max !== undefined ? opts.y_axis_boundaries.max : maxYValue;
	const y = d3.scaleLinear()
		.domain([yMin, yMax]).nice()
		.rangeRound([height - marginBottom, chartMarginTop + LABEL_HEIGHT]);

	// Create the SVG container.
	const svg = d3.create('svg')
		.attr('width', drawWidth)
		.attr('height', height)
		.attr('viewBox', [0, 0, drawWidth, height])
		.attr('style', drawWidth > width ? 'flex-shrink: 0;' : 'max-width: 100%; height: auto;');

	// Draw horizontal grid lines for y ticks behind the bars.
	const yGrid = d3.axisLeft(y)
		.ticks(6)
		.tickFormat(() => '')
		.tickSize(-(drawWidth - marginLeft - marginRight));
	svg.append('g')
		.attr('transform', `translate(${marginLeft},0)`)
		.call(yGrid)
		.call(g => g.selectAll('line').attr('stroke', BorderColor2).attr('stroke-opacity', 0.9))
		.call(g => g.select('.domain').remove());

	if (showLegend) {
		createLegend(svg, {
			sortedKeys,
			width,
			marginLeft,
			marginRight,
			marginTop,
			leftPosition: 0,
			itemSpacing: opts?.legendItemSpacing,
			maxItems: opts?.max_legend_items,
			legendOrientation: opts?.legend_orientation
		});
	}

	// Create reusable tooltip
	const tooltipDiv = createTooltip({ tooltipId });

	let selectedKey: string | null = null;
	const baseFillFallback = PrimaryColor;

	// readable text color for a background color
	const contrastTextFor = (col: string) => {
		const c = d3.color(col);
		if (!c) return 'white';
		const rgb = c.rgb();
		const lum = (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
		return lum > 0.8 ? SidelyBlack : 'white';
	};

	// Append a group for each displayName, and a rect for each group.
	const groupG = svg.append('g')
		.selectAll()
		.data(d3.group(enriched, d => d.displayName))
		.join('g')
		.attr('transform', ([name]) => `translate(${fx(name as string)},0)`);

	// create bars inside each name group with spacing and rounded top corners
	const BAR_GAP = opts?.stacked ? 0 : 4;
	const roundedTopPath = (x0: number, y0: number, w: number, h: number, r: number) => {
		if (w <= 0 || h <= 0) return '';
		const radius = Math.min(r, w / 2, h / 2);
		// start at bottom-left
		return `M ${x0} ${y0 + h} L ${x0} ${y0 + radius} A ${radius} ${radius} 0 0 1 ${x0 + radius} ${y0} L ${x0 + w - radius} ${y0} A ${radius} ${radius} 0 0 1 ${x0 + w} ${y0 + radius} L ${x0 + w} ${y0 + h} Z`;
	};

	if (opts?.stacked && stackedData) {
		// Stacked bars: each name gets one full-width bar with stacked segments
		const stackGroups = svg.append('g')
			.selectAll()
			.data(stackedData)
			.join('g')
			.attr('class', 'stack-group')
			.attr('fill', (d) => getGroupColor(d.key) ?? PrimaryColor);

		stackGroups.selectAll('path')
			.data(d => d)
			.join('path')
			.attr('class', 'bar-rect')
			.attr('d', (d, i) => {
				const nameIndex = i % namesOrder.length;

				const barX = fx(namesOrder[nameIndex]) as number;
				const barW = fx.bandwidth();
				const yTop = y(d[1]) as number;
				const yBottom = y(d[0]) as number;
				const h = yBottom - yTop;
				const isTopSegment = h > 0 && d[1] >= stackTotals[nameIndex];

				return roundedTopPath(barX, yTop, barW, h, isTopSegment ? BAR_RADIUS : 0);
			})
			.attr('data-name', (d, i) => namesOrder[i % namesOrder.length])
			.attr('data-group', (d, i, nodes) => {
				// Get the parent group element and extract the series key
				const element = nodes[i] as SVGPathElement;
				if (element.parentNode) {
					const parentElement = element.parentNode as SVGGElement;
					const seriesData = d3.select(parentElement).datum() as { key: string };
					return seriesData.key;
				}
				return '';
			})
			.attr('data-value', d => d[1] - d[0]);
	} else {
		// Grouped bars: side-by-side bars within each name group
		groupG.selectAll()
			.data(([, d]) => d)
			.join('path')
			.attr('class', 'bar-rect')
			.attr('d', (d) => {
				const raw = x.bandwidth();
				const barW = Math.max(0, raw - BAR_GAP);
				const offset = (raw - barW) / 2;
				const xPos = (x(d.group) as number) + offset;
				const yTop = y(d.value) as number;
				const h = (y(0) as number) - yTop;
				return roundedTopPath(xPos, yTop, barW, h, BAR_RADIUS);
			})
			.attr('fill', d => {
				try { return getGroupColor(d.group) ?? baseFillFallback; } catch { return baseFillFallback; }
			})
			.attr('data-name', d => d.displayName || d.name)
			.attr('data-group', d => d.group)
			.attr('data-value', d => d.value);
	}

	// Add permanent total value labels for stacked bars
	if (opts?.stacked && stackedData) {
		// Calculate total value for each name (sum across all groups)
		const getTotalStackValue = (name: string): number => {
			const nameIndex = namesOrder.findIndex(n => n === name);
			if (nameIndex === -1) return 0;

			return d3.sum(stackedData!, series => {
				const dataPoint = series[nameIndex];
				return dataPoint ? dataPoint[1] - dataPoint[0] : 0;
			});
		};

		// Add text labels showing total values above each stack
		svg.selectAll('.stack-total-label')
			.data(namesOrder)
			.join('text')
			.attr('class', 'stack-total-label')
			.attr('x', d => (fx(d) as number) + fx.bandwidth() / 2)
			.attr('y', d => {
				const total = getTotalStackValue(d);
				return y(total) - 6;
			})
			.attr('text-anchor', 'middle')
			.attr('font-size', '10px')
			.attr('font-weight', '500')
			.attr('fill', 'var(--on-surface-color)')
			.style('pointer-events', 'none')
			.text(d => {
				const total = getTotalStackValue(d);
				return formatValue(roundDigitsAfterDecimal(total, opts?.digits_after_decimal), opts);
			})
			.style('display', function(this: SVGTextElement) {
				return (this.textContent?.length ?? 0) * LABEL_CHAR_WIDTH + 4 > fx.step() ? 'none' : null;
			});
	}

	// Value labels above grouped bars. Only shown for a single group to avoid clutter.
	if (!opts?.stacked && groupsArr.length === 1) {
		svg.append('g')
			.attr('class', 'bar-labels')
			.selectAll()
			.data(enriched)
			.join('text')
			.attr('class', 'bar-value-label')
			.attr('x', d => (fx(d.displayName || d.name) as number) + (x(d.group) as number) + x.bandwidth() / 2)
			.attr('y', d => (y(d.value) as number) - 6)
			.attr('text-anchor', 'middle')
			.attr('fill', 'var(--on-surface-color)')
			.style('font-size', '10px')
			.style('font-weight', '500')
			.style('pointer-events', 'none')
			.text(d => formatValue(roundDigitsAfterDecimal(d.value, opts?.digits_after_decimal), opts))
			.style('display', function(this: SVGTextElement) {
				return (this.textContent?.length ?? 0) * LABEL_CHAR_WIDTH + 4 > fx.step() ? 'none' : null;
			});
	}

	// Add a transparent overlay per name group so hovering anywhere above the grouped bars
	// (not only directly over individual rects) will show the tooltip and hover line.
	if (!opts?.stacked) {
		// For grouped bars, keep the existing overlay logic
		groupG.append('rect')
			.attr('class', 'group-overlay')
			.attr('x', 0)
			.attr('y', chartMarginTop)
			.attr('width', fx.bandwidth())
			.attr('height', height - chartMarginTop - marginBottom)
			.style('fill', 'transparent')
			.style('pointer-events', 'all');
	}

	// Helper functions to centralize tooltip / hover behavior so overlays and rects behave the same.

	const showTooltipFor = (d: GroupedValueNameData & { displayName?: string }, _event?: MouseEvent, stackInfo?: { stackStart: number; stackEnd: number }) => {
		const base = getGroupColor(d.group) ?? baseFillFallback;

		// show focus label above the bar (instead of brightening the bar)
		const fxX = fx(d.displayName || d.name) as number;

		let xMid: number;
		let yBarTop: number;

		if (opts?.stacked) {
			xMid = fxX + fx.bandwidth() / 2;
			// For stacked bars, position focus in the middle of the specific segment
			if (stackInfo) {
				const segmentMiddle = (stackInfo.stackStart + stackInfo.stackEnd) / 2;
				yBarTop = y(segmentMiddle) as number;
			} else {
				yBarTop = y(maxYValue) as number;
			}
		} else {
			xMid = fxX + (x(d.group) as number) + x.bandwidth() / 2;
			yBarTop = y(d.value) as number;
		}

		focus.style('display', 'block')
			.attr('transform', `translate(${xMid},${yBarTop - 8})`);
		focus.select('rect.d3-focus-bg').attr('fill', base);
		focus.select('text').text(formatValue(roundDigitsAfterDecimal(d.value, opts?.digits_after_decimal), opts)).attr('fill', contrastTextFor(base));
		const textNode = focus.select('text').node() as SVGTextElement | null;
		if (textNode) {
			const bbox = textNode.getBBox();
			const padX = 6;
			const padY = 2;
			focus.select('rect.d3-focus-bg')
				.attr('x', bbox.x - padX)
				.attr('y', bbox.y - padY)
				.attr('width', bbox.width + padX * 2)
				.attr('height', bbox.height + padY * 2);
		}

		// Only show hover line for grouped bars, not stacked bars
		if (!opts?.stacked) {
			hoverLine.attr('x1', xMid).attr('x2', xMid).attr('y1', chartMarginTop).attr('y2', yBarTop).style('display', 'block');
		}

		tooltipDiv.innerHTML = `<div style="font-weight:600">${d.displayName || d.name}${d.group ? ' — ' + d.group : ''}</div>`;
		tooltipDiv.style.display = 'block';
	};

	const moveTooltipFor = (d: GroupedValueNameData & { displayName?: string }, event: MouseEvent, stackInfo?: { stackStart: number; stackEnd: number }) => {
		const base = getGroupColor(d.group) ?? baseFillFallback;

		// Reset non-selected bars back to their color (keep any clicked selection intact)
		svg.selectAll<SVGPathElement, unknown>('.bar-rect')
			.filter(function(this: SVGPathElement) {
				const elName = this.getAttribute('data-name') || '';
				const elGroup = this.getAttribute('data-group') || '';
				const elValue = parseFloat(this.getAttribute('data-value') || '0');
				const elKey = `${elName}__${elGroup}__${elValue}`;
				return selectedKey !== elKey;
			})
			.transition().duration(120)
			.attr('fill', function(this: SVGPathElement) {
				const elGroup = this.getAttribute('data-group') || '';
				return getGroupColor(elGroup) ?? baseFillFallback;
			});

		// show/update focus label above the nearest bar
		const fxX = fx(d.displayName || d.name) as number;

		let xMid: number;
		let yBarTop: number;

		if (opts?.stacked) {
			xMid = fxX + fx.bandwidth() / 2;
			// For stacked bars, position focus in the middle of the specific segment
			if (stackInfo) {
				const segmentMiddle = (stackInfo.stackStart + stackInfo.stackEnd) / 2;
				yBarTop = y(segmentMiddle) as number;
			} else {
				yBarTop = y(maxYValue) as number;
			}
		} else {
			xMid = fxX + (x(d.group) as number) + x.bandwidth() / 2;
			yBarTop = y(d.value) as number;
		}

		focus.style('display', 'block').attr('transform', `translate(${xMid},${yBarTop - 8})`);
		focus.select('rect.d3-focus-bg').attr('fill', base);
		focus.select('text').text(formatValue(roundDigitsAfterDecimal(d.value, opts?.digits_after_decimal), opts)).attr('fill', contrastTextFor(base));
		const textNode = focus.select('text').node() as SVGTextElement | null;
		if (textNode) {
			const bbox = textNode.getBBox();
			const padX = 6;
			const padY = 2;
			focus.select('rect.d3-focus-bg')
				.attr('x', bbox.x - padX)
				.attr('y', bbox.y - padY)
				.attr('width', bbox.width + padX * 2)
				.attr('height', bbox.height + padY * 2);
		}

		// Update tooltip contents (positioning handled below)
		tooltipDiv.innerHTML = `<div style="font-weight:600">${d.displayName || d.name}${d.group ? ' — ' + d.group : ''}</div>`;

		// Position tooltip using reusable function
		positionTooltip(tooltipDiv, event);

		// Only show hover line for grouped bars, not stacked bars
		if (!opts?.stacked) {
			hoverLine.attr('x1', xMid).attr('x2', xMid).attr('y1', chartMarginTop).attr('y2', yBarTop);
		}
	};

	const hideTooltipFor = (d: GroupedValueNameData | null) => {
		if (!d) {
			hideTooltip(tooltipDiv);
			// Only hide hover line for grouped bars
			if (!opts?.stacked) {
				hoverLine.style('display', 'none');
			}
			focus.style('display', 'none');
			return;
		}
		const key = keyFor(d);
		// hide focus label unless selected
		if (selectedKey !== key) focus.style('display', 'none');
		hideTooltip(tooltipDiv);
		// Only hide hover line for grouped bars
		if (!opts?.stacked) {
			hoverLine.style('display', 'none');
		}
	};

	const clickTooltipFor = (d: GroupedValueNameData & { displayName?: string }, event: MouseEvent, stackInfo?: { stackStart: number; stackEnd: number }) => {
		const key = keyFor(d);
		const base = getGroupColor(d.group) ?? baseFillFallback;
		const fxX = fx(d.displayName || d.name) as number;

		let xMid: number;
		let yBarTop: number;

		if (opts?.stacked) {
			xMid = fxX + fx.bandwidth() / 2;
			// For stacked bars, position focus in the middle of the specific segment
			if (stackInfo) {
				const segmentMiddle = (stackInfo.stackStart + stackInfo.stackEnd) / 2;
				yBarTop = y(segmentMiddle) as number;
			} else {
				yBarTop = y(maxYValue) as number;
			}
		} else {
			xMid = fxX + (x(d.group) as number) + x.bandwidth() / 2;
			yBarTop = y(d.value) as number;
		}

		if (selectedKey === key) {
			selectedKey = null;
			focus.style('display', 'none');
			tooltipDiv.style.display = 'none';
			// Only hide hover line for grouped bars
			if (!opts?.stacked) {
				hoverLine.style('display', 'none');
			}
		} else {
			// restore others
			svg.selectAll<SVGPathElement, unknown>('.bar-rect')
				.transition().duration(120)
				.attr('fill', function(this: SVGPathElement) {
					const elGroup = this.getAttribute('data-group') || '';
					return getGroupColor(elGroup) ?? baseFillFallback;
				});
			selectedKey = key;
			focus.style('display', 'block').attr('transform', `translate(${xMid},${yBarTop - 8})`);
			focus.select('rect.d3-focus-bg').attr('fill', base);
			focus.select('text').text(formatValue(roundDigitsAfterDecimal(d.value, opts?.digits_after_decimal), opts)).attr('fill', contrastTextFor(base));
			const textNode = focus.select('text').node() as SVGTextElement | null;
			if (textNode) {
				const bbox = textNode.getBBox();
				const padX = 6;
				const padY = 2;
				focus.select('rect.d3-focus-bg')
					.attr('x', bbox.x - padX)
					.attr('y', bbox.y - padY)
					.attr('width', bbox.width + padX * 2)
					.attr('height', bbox.height + padY * 2);
			}

			tooltipDiv.style.display = 'block';
			tooltipDiv.innerHTML = `<div style="font-weight:600">${d.displayName || d.name}${d.group ? ' — ' + d.group : ''}</div>`;

			let xPos = (event as MouseEvent & { pageX: number }).pageX + 12;
			let yPos = (event as MouseEvent & { pageY: number }).pageY - 12;
			tooltipDiv.style.left = `${xPos}px`;
			tooltipDiv.style.top = `${yPos}px`;
			tooltipDiv.style.display = 'block';
			const nodeRect = tooltipDiv.getBoundingClientRect();
			const margin = 8;
			if (nodeRect.right > window.innerWidth - margin) xPos = (event as MouseEvent & { pageX: number }).pageX - nodeRect.width - 12;
			if (nodeRect.top < margin) yPos = (event as MouseEvent & { pageY: number }).pageY + 12;
			tooltipDiv.style.left = `${xPos}px`;
			tooltipDiv.style.top = `${yPos}px`;

			// Only show hover line for grouped bars
			if (!opts?.stacked) {
				hoverLine.attr('x1', xMid).attr('x2', xMid).attr('y1', chartMarginTop).attr('y2', yBarTop).style('display', 'block');
			}
		}
	};

	// Attach handlers to overlays based on chart type
	if (!opts?.stacked) {
		// For grouped bars, keep existing overlay logic
		groupG.selectAll('rect.group-overlay')
			.on('mouseover', function(event, d) {
				const entry = d as unknown as [string, GroupedValueNameData[]];
				const arr = entry[1];
				if (!arr || arr.length === 0) return;
				const localX = d3.pointer(event, event.currentTarget as Element)[0];
				// find nearest bar in this group by center distance
				let nearest = arr[0];
				let minDist = Infinity;
				for (const datum of arr) {
					const center = (x(datum.group) as number) + x.bandwidth() / 2;
					const dist = Math.abs(center - localX);
					if (dist < minDist) { minDist = dist; nearest = datum; }
				}
				showTooltipFor(nearest, event as MouseEvent);
			})
			.on('mousemove', function(event, d) {
				const entry = d as unknown as [string, GroupedValueNameData[]];
				const arr = entry[1];
				if (!arr || arr.length === 0) return;
				const localX = d3.pointer(event, event.currentTarget as Element)[0];
				let nearest = arr[0];
				let minDist = Infinity;
				for (const datum of arr) {
					const center = (x(datum.group) as number) + x.bandwidth() / 2;
					const dist = Math.abs(center - localX);
					if (dist < minDist) { minDist = dist; nearest = datum; }
				}
				moveTooltipFor(nearest, event as MouseEvent);
			})
			.on('mouseout', function(event, d) {
				const entry = d as unknown as [string, GroupedValueNameData[]];
				const arr = entry[1];
				if (!arr || arr.length === 0) { hideTooltipFor(null); return; }
				// hide unless selected stays
				hideTooltipFor(arr[0]);
			})
			.on('click', function(event, d) {
				const entry = d as unknown as [string, GroupedValueNameData[]];
				const arr = entry[1];
				if (!arr || arr.length === 0) return;
				const localX = d3.pointer(event, event.currentTarget as Element)[0];
				let nearest = arr[0];
				let minDist = Infinity;
				for (const datum of arr) {
					const center = (x(datum.group) as number) + x.bandwidth() / 2;
					const dist = Math.abs(center - localX);
					if (dist < minDist) { minDist = dist; nearest = datum; }
				}
				clickTooltipFor(nearest, event as MouseEvent);
			});
	}

	// Hover line (vertical) for grouped vertical bars.
	const hoverLine = svg.append('line')
		.attr('class', `hover-line-${tooltipId ?? 'global'}`)
		.style('display', 'none')
		.attr('stroke', BorderColor2)
		.attr('stroke-opacity', 0.9)
		.attr('stroke-dasharray', '4,3')
		.attr('stroke-width', 1.25);

	// Focus label (rect background + text) shown above a bar instead of brightening the bar.
	// Redundant when the chart already prints its values, so keep it hidden in that case.
	const focus = svg.append('g').attr('class', 'd3-focus').style('display', 'none');
	if (showsValueLabels) focus.style('visibility', 'hidden');
	focus.append('rect').attr('class', 'd3-focus-bg').attr('rx', 4).attr('ry', 4).style('pointer-events', 'none');
	focus.append('text')
		.attr('class', 'd3-focus-text')
		.attr('x', 0)
		.attr('text-anchor', 'middle')
		.attr('y', '-6')
		.attr('font-size', 11)
		.attr('font-weight', '600')
		.style('pointer-events', 'none');

	// interaction handlers attached to actual rects (grouped bars)
	const keyFor = (d: GroupedValueNameData & { displayName?: string }) => `${d.displayName || d.name}__${d.group}__${d.value}`;

	// attach handlers to path.bar-rect elements (we draw bars as paths for rounded corners)
	svg.selectAll<SVGPathElement, GroupedValueNameData>('.bar-rect')
		.on('mouseover', function(event: MouseEvent) {
			// Get data from attributes for both stacked and grouped bars
			const element = event.currentTarget as SVGPathElement;
			const name = element.getAttribute('data-name') || '';
			const group = element.getAttribute('data-group') || '';
			const value = parseFloat(element.getAttribute('data-value') || '0');

			const d: GroupedValueNameData & { displayName: string } = {
				name: name,
				group: group,
				value: value,
				displayName: name,
				nodeValue: { text: name } // Using text object as nodeValue since original is not available
			};

			const base = getGroupColor(d.group) ?? baseFillFallback;
			const fxX = fx(d.displayName || d.name) as number;

			let xMid: number;
			let yBarTop: number;

			if (opts?.stacked) {
				xMid = fxX + fx.bandwidth() / 2;
				// For stacked bars from direct bar hover, find the actual segment position
				if (stackedData) {
					const nameIndex = namesOrder.findIndex(n => n === name);
					const series = stackedData.find(s => s.key === group);
					if (series && nameIndex >= 0 && nameIndex < series.length) {
						const dataPoint = series[nameIndex];
						const segmentMiddle = (dataPoint[0] + dataPoint[1]) / 2;
						yBarTop = y(segmentMiddle) as number;
					} else {
						yBarTop = y(d.value) as number;
					}
				} else {
					yBarTop = y(d.value) as number;
				}
			} else {
				xMid = fxX + (x(d.group) as number) + x.bandwidth() / 2;
				yBarTop = y(d.value) as number;
			}

			focus.style('display', 'block').attr('transform', `translate(${xMid},${yBarTop - 8})`);
			focus.select('rect.d3-focus-bg').attr('fill', base);
			focus.select('text').text(formatValue(roundDigitsAfterDecimal(d.value, opts?.digits_after_decimal), opts)).attr('fill', contrastTextFor(base));
			const textNode = focus.select('text').node() as SVGTextElement | null;
			if (textNode) {
				const bbox = textNode.getBBox();
				const padX = 6;
				const padY = 2;
				focus.select('rect.d3-focus-bg')
					.attr('x', bbox.x - padX)
					.attr('y', bbox.y - padY)
					.attr('width', bbox.width + padX * 2)
					.attr('height', bbox.height + padY * 2);
			}

			// Only show hover line for grouped bars, not stacked bars
			if (!opts?.stacked) {
				hoverLine.attr('x1', xMid).attr('x2', xMid).attr('y1', chartMarginTop).attr('y2', yBarTop).style('display', 'block');
			}

			tooltipDiv.style.display = 'block';
			tooltipDiv.innerHTML = `<div style="font-weight:600">${d.displayName || d.name}${d.group ? ' — ' + d.group : ''}</div>`;
		})
		.on('mousemove', function(event: MouseEvent) {
			// Get data from attributes
			const element = event.currentTarget as SVGPathElement;
			const name = element.getAttribute('data-name') || '';
			const group = element.getAttribute('data-group') || '';
			const value = parseFloat(element.getAttribute('data-value') || '0');

			const d: GroupedValueNameData & { displayName: string } = {
				name: name,
				group: group,
				value: value,
				displayName: name,
				nodeValue: { text: name } // Using name as nodeValue since original is not available
			};

			const e = event as MouseEvent & { pageX: number; pageY: number };
			const margin = 8;
			let xPos = e.pageX + 12;
			let yPos = e.pageY - 12;
			tooltipDiv.style.left = `${xPos}px`;
			tooltipDiv.style.top = `${yPos}px`;
			tooltipDiv.style.display = 'block';
			const rect = tooltipDiv.getBoundingClientRect();
			if (rect.right > window.innerWidth - margin) xPos = e.pageX - rect.width - 12;
			if (rect.top < margin) yPos = e.pageY + 12;
			tooltipDiv.style.left = `${xPos}px`;
			tooltipDiv.style.top = `${yPos}px`;
			// keep hover line snapped to bar center and update focus label
			const fxX = fx(d.displayName || d.name) as number;

			let xMid: number;
			let yBarTop: number;

			if (opts?.stacked) {
				xMid = fxX + fx.bandwidth() / 2;
				// For stacked bars from direct bar hover, find the actual segment position
				if (stackedData) {
					const nameIndex = namesOrder.findIndex(n => n === name);
					const series = stackedData.find(s => s.key === group);
					if (series && nameIndex >= 0 && nameIndex < series.length) {
						const dataPoint = series[nameIndex];
						const segmentMiddle = (dataPoint[0] + dataPoint[1]) / 2;
						yBarTop = y(segmentMiddle) as number;
					} else {
						yBarTop = y(d.value) as number;
					}
				} else {
					yBarTop = y(d.value) as number;
				}
			} else {
				xMid = fxX + (x(d.group) as number) + x.bandwidth() / 2;
				yBarTop = y(d.value) as number;
			}

			// Only show hover line for grouped bars, not stacked bars
			if (!opts?.stacked) {
				hoverLine.attr('x1', xMid).attr('x2', xMid).attr('y1', chartMarginTop).attr('y2', yBarTop);
			}

			focus.style('display', 'block').attr('transform', `translate(${xMid},${yBarTop - 8})`);
			const base = getGroupColor(d.group) ?? baseFillFallback;
			focus.select('rect.d3-focus-bg').attr('fill', base);
			focus.select('text').text(formatValue(roundDigitsAfterDecimal(d.value, opts?.digits_after_decimal), opts)).attr('fill', contrastTextFor(base));
			const textNode = focus.select('text').node() as SVGTextElement | null;
			if (textNode) {
				const bbox = textNode.getBBox();
				const padX = 6;
				const padY = 2;
				focus.select('rect.d3-focus-bg')
					.attr('x', bbox.x - padX)
					.attr('y', bbox.y - padY)
					.attr('width', bbox.width + padX * 2)
					.attr('height', bbox.height + padY * 2);
			}
		})
		.on('mouseout', function(event: MouseEvent) {
			// Get data from attributes
			const element = event.currentTarget as SVGPathElement;
			const name = element.getAttribute('data-name') || '';
			const group = element.getAttribute('data-group') || '';
			const value = parseFloat(element.getAttribute('data-value') || '0');

			const d: GroupedValueNameData & { displayName: string } = {
				name: name,
				group: group,
				value: value,
				displayName: name,
				nodeValue: { text: name } // Using name as nodeValue since original is not available
			};

			const key = keyFor(d);
			const idxSelected = selectedKey;

			if (idxSelected !== key) {
				// restore to original color scale and hide focus label
				const base = getGroupColor(d.group) ?? baseFillFallback;
				const targetBar = svg.selectAll<SVGPathElement, unknown>('.bar-rect')
					.filter(function(this: SVGPathElement) {
						const elName = this.getAttribute('data-name') || '';
						const elGroup = this.getAttribute('data-group') || '';
						const elValue = parseFloat(this.getAttribute('data-value') || '0');
						const elKey = `${elName}__${elGroup}__${elValue}`;
						return elKey === key;
					});
				targetBar.transition().duration(120).attr('fill', base);
				tooltipDiv.style.display = 'none';
				// Only hide hover line for grouped bars
				if (!opts?.stacked) {
					hoverLine.style('display', 'none');
				}
				focus.style('display', 'none');
			}
		})
		.on('click', function(event: MouseEvent) {
			// Get data from attributes
			const element = event.currentTarget as SVGPathElement;
			const name = element.getAttribute('data-name') || '';
			const group = element.getAttribute('data-group') || '';
			const value = parseFloat(element.getAttribute('data-value') || '0');

			const d: GroupedValueNameData & { displayName: string } = {
				name: name,
				group: group,
				value: value,
				displayName: name,
				nodeValue: { text: name } // Using name as nodeValue since original is not available
			};

			const key = keyFor(d);
			const base = getGroupColor(d.group) ?? baseFillFallback;
			const fxX = fx(d.displayName || d.name) as number;

			let xMid: number;
			let yBarTop: number;

			if (opts?.stacked) {
				xMid = fxX + fx.bandwidth() / 2;
				// For stacked bars from direct bar hover, find the actual segment position
				if (stackedData) {
					const nameIndex = namesOrder.findIndex(n => n === name);
					const series = stackedData.find(s => s.key === group);
					if (series && nameIndex >= 0 && nameIndex < series.length) {
						const dataPoint = series[nameIndex];
						const segmentMiddle = (dataPoint[0] + dataPoint[1]) / 2;
						yBarTop = y(segmentMiddle) as number;
					} else {
						yBarTop = y(d.value) as number;
					}
				} else {
					yBarTop = y(d.value) as number;
				}
			} else {
				xMid = fxX + (x(d.group) as number) + x.bandwidth() / 2;
				yBarTop = y(d.value) as number;
			}

			if (selectedKey === key) {
				selectedKey = null;
				// restore visual state
				svg.selectAll<SVGPathElement, GroupedValueNameData>('.bar-rect')
					.transition().duration(120)
					.attr('fill', function(this: SVGPathElement) {
						const elGroup = this.getAttribute('data-group') || '';
						return getGroupColor(elGroup) ?? baseFillFallback;
					});
				tooltipDiv.style.display = 'none';
				// Only hide hover line for grouped bars
				if (!opts?.stacked) {
					hoverLine.style('display', 'none');
				}
				focus.style('display', 'none');
			} else {
				// reset others
				svg.selectAll<SVGPathElement, unknown>('.bar-rect')
					.transition().duration(120)
					.attr('fill', function(this: SVGPathElement) {
						const elGroup = this.getAttribute('data-group') || '';
						return getGroupColor(elGroup) ?? baseFillFallback;
					});

				selectedKey = key;
				focus.style('display', 'block').attr('transform', `translate(${xMid},${yBarTop - 8})`);
				focus.select('rect.d3-focus-bg').attr('fill', base);
				focus.select('text').text(formatValue(roundDigitsAfterDecimal(d.value, opts?.digits_after_decimal), opts)).attr('fill', contrastTextFor(base));
				const textNode = focus.select('text').node() as SVGTextElement | null;
				if (textNode) {
					const bbox = textNode.getBBox();
					const padX = 6;
					const padY = 2;
					focus.select('rect.d3-focus-bg')
						.attr('x', bbox.x - padX)
						.attr('y', bbox.y - padY)
						.attr('width', bbox.width + padX * 2)
						.attr('height', bbox.height + padY * 2);
				}

				tooltipDiv.style.display = 'block';
				tooltipDiv.innerHTML = `<div style="font-weight:600">${d.displayName || d.name} — ${d.group}</div>`;

				let xPos = (event as MouseEvent & { pageX: number }).pageX + 12;
				let yPos = (event as MouseEvent & { pageY: number }).pageY - 12;
				tooltipDiv.style.left = `${xPos}px`;
				tooltipDiv.style.top = `${yPos}px`;
				tooltipDiv.style.display = 'block';
				const nodeRect = tooltipDiv.getBoundingClientRect();
				const margin = 8;
				if (nodeRect.right > window.innerWidth - margin) xPos = (event as MouseEvent & { pageX: number }).pageX - nodeRect.width - 12;
				if (nodeRect.top < margin) yPos = (event as MouseEvent & { pageY: number }).pageY + 12;
				tooltipDiv.style.left = `${xPos}px`;
				tooltipDiv.style.top = `${yPos}px`;

				// Only show hover line for grouped bars
				if (!opts?.stacked) {
					hoverLine.attr('x1', xMid).attr('x2', xMid).attr('y1', chartMarginTop).attr('y2', yBarTop).style('display', 'block');
				}
			}
		});

	// Append the horizontal axis, skipping ticks whose labels would overlap.
	const tickLabelWidth = Math.max(0, ...namesOrder.map(n => trimLabel(String(n), opts?.trim_x_labels).length)) * LABEL_CHAR_WIDTH + 8;
	const tickStep = Math.max(1, Math.ceil(tickLabelWidth / Math.max(1, fx.bandwidth())));
	svg.append('g')
		.attr('transform', `translate(0,${height - marginBottom})`)
		.call(d3.axisBottom(fx).tickSize(0).tickValues(namesOrder.filter((_, i) => i % tickStep === 0)).tickFormat((d: unknown) => trimLabel(String(d), opts?.trim_x_labels)))
		.call(g => g.selectAll('.domain').remove())
		.call(g => g.selectAll('text').each(function(this: SVGTextElement, d) {
			d3.select(this).append('title').text(String(d));
		}));

	// Append the vertical axis.
	svg.append('g')
		.attr('transform', `translate(${marginLeft},0)`)
		.call(d3.axisLeft(y).ticks(null, 's').tickFormat((d: d3.NumberValue) => {
			const value = roundDigitsAfterDecimal(d as number, opts?.digits_after_decimal);
			const formatted = d3.format('~s')(value);
			return opts?.trim_y_labels ? trimLabel(formatted, opts.trim_y_labels) : formatted;
		}))
		.call(g => g.selectAll('.domain').remove())
		.call(g => g.selectAll('text').each(function(this: SVGTextElement, d) {
			const value = roundDigitsAfterDecimal(d as number, opts?.digits_after_decimal);
			const formatted = d3.format('~s')(value);
			d3.select(this).append('title').text(formatted);
		}));

	// Add x-axis legend (horizontal) if provided
	if (opts?.x_axis_legend) {
		svg.append('text')
			.attr('class', 'x-axis-legend')
			.attr('text-anchor', 'middle')
			.attr('x', marginLeft + (drawWidth - marginLeft - marginRight) / 2)
			.attr('y', height - 10)
			.attr('font-size', '12px')
			.attr('font-weight', '500')
			.attr('fill', 'var(--on-surface-color)')
			.text(opts.x_axis_legend);
	}

	// Add y-axis legend (vertical) if provided
	if (opts?.y_axis_legend) {
		svg.append('text')
			.attr('class', 'y-axis-legend')
			.attr('text-anchor', 'middle')
			.attr('transform', `translate(15, ${chartMarginTop + (height - chartMarginTop - marginBottom) / 2}) rotate(-90)`)
			.attr('font-size', '12px')
			.attr('font-weight', '500')
			.attr('fill', 'var(--on-surface-color)')
			.text(opts.y_axis_legend);
	}

	// Return the chart SVG node.
	// Setup global tooltip hiding with cleanup
	const cleanup = setupTooltipHiding(tooltipDiv, () => {
		hoverLine.style('display', 'none');
		selectedKey = null;
		// restore original colors
		svg.selectAll<SVGPathElement, unknown>('.bar-rect')
			.transition().duration(120)
			.attr('fill', function(this: SVGPathElement) {
				const elGroup = this.getAttribute('data-group') || '';
				return getGroupColor(elGroup) ?? baseFillFallback;
			});
		focus.style('display', 'none');
	});

	const svgNode = svg.node();
	if (!svgNode) return null;

	// Store cleanup function on the SVG element
	(svgNode as ElementWithCleanup)._cleanupTooltip = cleanup;

	return svgNode;
}
